/**
 * Device claiming, device settings, and member management — task P3-06.
 *
 * The claim form is the one screen where error text matters more than layout.
 * A user standing at a vehicle with a label in their hand needs to know
 * whether to retype the code, upgrade their plan, or give up — and the server
 * deliberately will not tell them whether the `ch_id` exists, so the wording
 * has to be honest about the ambiguity rather than guess.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
    ApiError,
    claimDevice,
    claimDeviceFromQr,
    listMembers,
    releaseDevice,
    updateDeviceSettings,
    updateMemberRole,
    type DeviceSummary,
    type Me,
    type Member,
} from "../api.js";

/** Maps a server error code to something a person can act on. */
function claimErrorMessage(error: unknown): string {
    if (!(error instanceof ApiError)) return String(error);

    switch (error.code) {
        case "invalid_code":
            // Both "no such ch_id" and "wrong code" arrive here, because the
            // server refuses to distinguish them. Saying "check both" is the
            // honest instruction.
            return "That device ID and claim code do not match. Check both — the code is on the same label.";
        case "invalid_qr":
            return "That is not a valid ChargeHawk manufacturing QR payload. Paste the JSON text from the device QR or manufacturing-qr-payload.json file.";
        case "already_claimed":
            return "This device is already claimed. If you bought it used, the previous owner needs to release it first.";
        case "device_limit_reached":
            return "Your plan is full. Release a device or upgrade to add another.";
        case "email_unverified":
            return "Confirm your email address first — check your inbox for the link.";
        case "rate_limited":
            return "Too many attempts. Try again in an hour.";
        default:
            return error.message;
    }
}

export function ClaimDeviceForm({ me, onClaimed }: {
    me: Me;
    onClaimed: () => void;
}) {
    const [chId, setChId] = useState("");
    const [code, setCode] = useState("");
    const [qrPayload, setQrPayload] = useState("");
    const [mode, setMode] = useState<"label" | "qr">("label");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const full = me.deviceCount >= me.plan.maxDevices;

    async function submit(event: FormEvent) {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            if (mode === "qr") {
                await claimDeviceFromQr(qrPayload.trim());
            } else {
                await claimDevice(chId.trim(), code.trim());
            }
            setChId("");
            setCode("");
            setQrPayload("");
            onClaimed();
        } catch (caught) {
            setError(claimErrorMessage(caught));
        } finally {
            setBusy(false);
        }
    }

    return (
        <form className="panel" onSubmit={submit}>
            <h2>Add a device</h2>
            <p className="note">
                {mode === "qr"
                    ? "Paste the complete JSON text from the device QR or manufacturing-qr-payload.json file."
                    : "Both values are printed on the label. Spacing, hyphens, and case do not matter."}
            </p>

            <div className="tabs" role="tablist" aria-label="Device adding method">
                <button
                    type="button"
                    className={mode === "label" ? "active" : undefined}
                    onClick={() => setMode("label")}
                    role="tab"
                    aria-selected={mode === "label"}
                >
                    Device ID and claim code
                </button>
                <button
                    type="button"
                    className={mode === "qr" ? "active" : undefined}
                    onClick={() => setMode("qr")}
                    role="tab"
                    aria-selected={mode === "qr"}
                >
                    Paste QR JSON
                </button>
            </div>

            {mode === "qr" ? (
                <div className="field-row">
                    <label>
                        <span>Manufacturing QR JSON</span>
                        <textarea
                            required
                            rows={5}
                            spellCheck={false}
                            placeholder='{"ver":"v1","name":"CH_..."}'
                            value={qrPayload}
                            onChange={(event) => setQrPayload(event.target.value)}
                        />
                    </label>
                    <button type="submit" disabled={busy || full}>
                        {busy ? "Adding…" : "Add device from QR"}
                    </button>
                </div>
            ) : (
                <div className="field-row">
                    <label>
                        <span>Device ID</span>
                        <input
                            required
                            placeholder="CH-000123"
                            value={chId}
                            onChange={(event) => setChId(event.target.value)}
                        />
                    </label>
                    <label>
                        <span>Claim code</span>
                        <input
                            required
                            placeholder="CH3K-9T2M"
                            value={code}
                            onChange={(event) => setCode(event.target.value)}
                        />
                    </label>
                    <button type="submit" disabled={busy || full}>
                        {busy ? "Adding…" : "Add device"}
                    </button>
                </div>
            )}

            {full ? (
                <p className="warning">
                    Your {me.plan.code} plan allows {me.plan.maxDevices} device
                    {me.plan.maxDevices === 1 ? "" : "s"}.
                </p>
            ) : null}
            {error ? <p className="error">{error}</p> : null}
        </form>
    );
}

export function DeviceSettings({ device, onChanged }: {
    device: DeviceSummary;
    onChanged: () => void;
}) {
    const [timezone, setTimezone] = useState(device.timezone);
    const [status, setStatus] = useState<string | null>(null);

    // A device row can change underneath this component when the picker moves.
    useEffect(() => setTimezone(device.timezone), [device.deviceId, device.timezone]);

    const zones =
        typeof Intl.supportedValuesOf === "function"
            ? Intl.supportedValuesOf("timeZone")
            : [device.timezone, "UTC"];

    async function save(event: FormEvent) {
        event.preventDefault();
        setStatus(null);
        try {
            await updateDeviceSettings(device.deviceId, timezone);
            setStatus("Saved.");
            onChanged();
        } catch (caught) {
            setStatus(caught instanceof ApiError ? caught.message : String(caught));
        }
    }

    async function release() {
        // Destructive and not obviously reversible without the label code, so
        // it asks first.
        if (
            !window.confirm(
                `Remove ${device.chId}? Its history stays with your account, but ` +
                    `you will need the claim code from the label to add it again.`,
            )
        ) {
            return;
        }
        await releaseDevice(device.deviceId);
        onChanged();
    }

    return (
        <form className="panel" onSubmit={save}>
            <h2>{device.chId}</h2>
            <div className="field-row">
                <label>
                    <span>Time zone</span>
                    <select
                        value={timezone}
                        onChange={(event) => setTimezone(event.target.value)}
                    >
                        {zones.includes(timezone) ? null : (
                            <option value={timezone}>{timezone}</option>
                        )}
                        {zones.map((zone) => (
                            <option key={zone} value={zone}>
                                {zone}
                            </option>
                        ))}
                    </select>
                    <small>Sets day boundaries on charts and, later, quiet hours.</small>
                </label>
                <button type="submit">Save</button>
                <button type="button" className="danger" onClick={release}>
                    Remove device
                </button>
            </div>
            {status ? <p className="note">{status}</p> : null}
        </form>
    );
}

export function MembersPanel({ me }: { me: Me }) {
    const [members, setMembers] = useState<Member[]>([]);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(() => {
        listMembers()
            .then((result) => setMembers(result.members))
            .catch((caught: unknown) => setError(String(caught)));
    }, []);

    useEffect(refresh, [refresh]);

    async function changeRole(userId: string, role: Member["role"]) {
        setError(null);
        try {
            await updateMemberRole(userId, role);
            refresh();
        } catch (caught) {
            setError(
                caught instanceof ApiError && caught.code === "last_owner"
                    ? "An account must keep at least one owner."
                    : String(caught),
            );
        }
    }

    const canEdit = me.account.role === "owner";

    return (
        <section className="panel">
            <h2>People</h2>
            <table className="members">
                <thead>
                    <tr>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Since</th>
                    </tr>
                </thead>
                <tbody>
                    {members.map((member) => (
                        <tr key={member.userId}>
                            <td>
                                {member.email}
                                {member.userId === me.user.id ? " (you)" : ""}
                            </td>
                            <td>
                                {canEdit ? (
                                    <select
                                        value={member.role}
                                        onChange={(event) =>
                                            changeRole(
                                                member.userId,
                                                event.target.value as Member["role"],
                                            )
                                        }
                                    >
                                        <option value="owner">owner</option>
                                        <option value="admin">admin</option>
                                        <option value="viewer">viewer</option>
                                    </select>
                                ) : (
                                    member.role
                                )}
                            </td>
                            <td>{new Date(member.joinedAt).toLocaleDateString()}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {error ? <p className="error">{error}</p> : null}
            <p className="note">
                Owners can transfer and remove devices. Admins can add devices and
                relay data from a phone. Viewers can only look.
            </p>
        </section>
    );
}
